import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Person,
  Phone,
  Email,
  Warning,
  LocalHospital,
  Receipt,
  Notes,
  Business,
  CreditCard,
  Visibility,
} from '@mui/icons-material';
import { format } from 'date-fns';
import apiClient from '../lib/axios';
import { PatientEditDialog } from '../components/PatientEditDialog';

interface PatientAllergy {
  id: string;
  rxcui: string | null;
  name: string;
  addedByDoctor: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface Patient {
  id: string;
  name: string;
  nationalIdType?: string;
  nationalId?: string;
  passportCountry?: string;
  phone?: string;
  email?: string;
  dateOfBirth?: string;
  allergies?: PatientAllergy[];
  chronicConditions?: string;
  insuranceCompanyId?: string;
  insuranceNumber?: string;
  generalNotes?: string;
  lastVisit?: string;
  createdAt: string;
  updatedAt: string;
  orders?: Order[];
  shareWithOtherDoctors: boolean;
}

interface Order {
  id: string;
  trackingUUID: string;
  status: string;
  totalAmount: string;
  itemCount: number;
  createdAt: string;
  pharmacyChain?: {
    id: string;
    name: string;
    logoUrl?: string;
  };
}

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  READY_FOR_PICKUP: 'primary',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const getStatusLabel = (status: string, t: (key: string) => string): string => {
  const labels: Record<string, string> = {
    PENDING: t('status.pending'),
    IN_PROGRESS: t('status.inProgress'),
    READY_FOR_PICKUP: t('status.readyForPickup'),
    COMPLETED: t('status.completed'),
    CANCELLED: t('status.cancelled'),
  };
  return labels[status] || status;
};

export const PatientDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: patient, isLoading, error } = useQuery({
    queryKey: ['patient', id],
    queryFn: async () => {
      const { data } = await apiClient.get<Patient>(`/patients/${id}`);
      return data;
    },
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/patients/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      navigate('/doctor/patients');
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !patient) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{t('patientDetail.loadError')}</Alert>
          <Button onClick={() => navigate('/doctor/patients')} sx={{ mt: 2 }}>
            {t('patientDetail.backToPatients')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/doctor/patients')}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {patient.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('patientDetail.patientSince', { date: format(new Date(patient.createdAt), 'MMM d, yyyy') })}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
            >
              {t('common.edit')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t('common.delete')}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> {t('patientDetail.contactInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Person sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('common.fullName')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.name}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CreditCard sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {patient.nationalIdType === 'PASSPORT' ? t('patient.passport') : t('patient.cedula')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.nationalId || t('patientDetail.notProvided')}
                    {patient.nationalIdType === 'PASSPORT' && patient.passportCountry && (
                      <span> ({patient.passportCountry})</span>
                    )}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Phone sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('patientDetail.phoneNumber')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.phone || t('patientDetail.notProvided')}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Email sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('common.email')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.email || t('patientDetail.notProvided')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Medical Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalHospital /> {t('patientDetail.medicalInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Warning sx={{ fontSize: 20, color: 'warning.main' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('patients.allergies')}
                    </Typography>
                  </Box>
                  {patient.allergies && patient.allergies.length > 0 ? (
                    <Box sx={{ ml: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {patient.allergies.map((allergy) => (
                        <Chip
                          key={allergy.id}
                          label={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {allergy.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                                Dr. {allergy.addedByDoctor.lastName}
                              </Typography>
                            </Box>
                          }
                          variant="outlined"
                          color="warning"
                          sx={{
                            height: 'auto',
                            '& .MuiChip-label': {
                              display: 'block',
                              whiteSpace: 'normal',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                      {t('patients.noneRecorded')}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <LocalHospital sx={{ fontSize: 20, color: 'info.main' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('patients.chronicConditions')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.chronicConditions || t('patients.noneRecorded')}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Business sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('patients.insuranceCompany')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.insuranceCompanyId || t('patientDetail.notProvided')}
                  </Typography>
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <CreditCard sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {t('patients.insuranceNumber')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" sx={{ ml: 4 }}>
                    {patient.insuranceNumber || t('patientDetail.notProvided')}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* General Notes */}
          {patient.generalNotes && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Notes /> {t('patients.generalNotes')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {patient.generalNotes}
                </Typography>
              </Paper>
            </Grid>
          )}

          {/* Prescription History */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Receipt /> {t('patientDetail.prescriptionHistory')} ({patient.orders?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {!patient.orders || patient.orders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Receipt sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('patientDetail.noPrescriptions')}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.date')}</TableCell>
                        <TableCell>{t('patientDetail.pharmacy')}</TableCell>
                        <TableCell>{t('common.status')}</TableCell>
                        <TableCell align="center">{t('common.items')}</TableCell>
                        <TableCell>{t('common.total')}</TableCell>
                        <TableCell align="right">{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patient.orders.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {format(new Date(order.createdAt), 'MMM d, yyyy')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(order.createdAt), 'h:mm a')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.pharmacyChain?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(order.status, t)}
                              color={statusColors[order.status] || 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={order.itemCount} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              ${parseFloat(order.totalAmount).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/doctor/orders/${order.id}`)}
                            >
                              <Visibility />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Edit Dialog */}
      {editDialogOpen && (
        <PatientEditDialog
          patient={patient}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('patientDetail.deletePatient')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('patientDetail.deleteConfirmation', { name: patient.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('patientDetail.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
